import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { formatLiftAPIClientError } from '~/components/form/utils';
import { carsViolationsRetrieve } from '~/data/generated/endpoints/cars/cars';
import { inspectionReportsSupersessionDecisionsCreate } from '~/data/generated/endpoints/inspection-reports/inspection-reports';
import type {
  SupersessionDecisionRequest as ApiSupersessionDecision,
  SupersessionDecisionRequestResolutionType,
} from '~/data/generated/model';
import type {
  SupersessionDecisionKind,
  SupersessionSummary,
} from '~/data/schemas/inspectionReport';
import useLAISnackbar from '~/hooks/useLAISnackbar';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { BUTTON_SX } from '~/theme';

interface OpenViolation {
  id: string;
  code: string;
  description: string;
}

interface DecisionState {
  kind: SupersessionDecisionKind;
  resolutionNotes: string;
  resolutionType: string;
  customResolutionType: string;
}

const DEFAULT_DECISION: DecisionState = {
  kind: 'carry_forward',
  resolutionNotes: '',
  resolutionType: 'reinspection',
  customResolutionType: '',
};

// A 'resolved' decision must carry notes (and a custom type when type is 'Other')
// or the endpoint rejects it.
const isDecisionComplete = (decision: DecisionState): boolean => {
  if (decision.kind !== 'resolved') {
    return true;
  }
  if (!decision.resolutionNotes.trim()) {
    return false;
  }
  return decision.resolutionType !== 'other' || decision.customResolutionType.trim().length > 0;
};

interface SupersessionDecisionsDialogProps {
  open: boolean;
  carId: string;
  reportId: string;
  onClose: () => void;
  onComplete?: (summary: SupersessionSummary) => void;
}

/**
 * After a new inspection report is saved, surfaces every open violation on the
 * lift and lets the user decide each one: carry forward (leave open), mark
 * superseded (the new report no longer mentions it), or mark resolved. Posts the
 * batch to the supersession-decisions endpoint. We never auto-supersede — a
 * defect the new inspector still observed must stay visible.
 */
export const SupersessionDecisionsDialog = ({
  open,
  carId,
  reportId,
  onClose,
  onComplete,
}: SupersessionDecisionsDialogProps) => {
  const { showSnackbar } = useLAISnackbar();
  const terms = useLocaleTerms();
  const violationsTerm = terms.violations.toLowerCase();
  const liftTerm = terms.vehicle.toLowerCase();
  const [violations, setViolations] = useState<OpenViolation[]>([]);
  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !carId) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    carsViolationsRetrieve(carId)
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        // The car-violations endpoint emits a capitalized status
        // ("Open"/"Overdue"/"Resolved"); normalize before comparing.
        const openViolations: OpenViolation[] = (data.results ?? [])
          .filter((v) => v.status.toLowerCase() !== 'resolved')
          .map((v) => ({
            id: String(v.id),
            code: v.code ?? '',
            description: v.description ?? '',
          }));
        setViolations(openViolations);
        setDecisions(
          Object.fromEntries(openViolations.map((v) => [v.id, { ...DEFAULT_DECISION }])),
        );
      })
      .catch(() => showSnackbar('Failed to load open violations', { variant: 'error' }))
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, carId, showSnackbar]);

  const patchDecision = (id: string, patch: Partial<DecisionState>) =>
    setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const handleSubmit = async () => {
    const decisionsPayload: ApiSupersessionDecision[] = violations.map((v) => {
      const decision = decisions[v.id] ?? DEFAULT_DECISION;
      if (decision.kind === 'resolved') {
        return {
          violationId: v.id,
          decision: 'resolved',
          resolutionNotes: decision.resolutionNotes,
          // The Select only emits reinspection/waiver/other, which is exactly the
          // generated enum; the form state types it as a bare string.
          resolutionType:
            decision.resolutionType as (typeof SupersessionDecisionRequestResolutionType)[keyof typeof SupersessionDecisionRequestResolutionType],
          customResolutionType:
            decision.resolutionType === 'other' ? decision.customResolutionType : null,
        };
      }
      return { violationId: v.id, decision: decision.kind };
    });

    setSubmitting(true);
    try {
      const { data: summary } = await inspectionReportsSupersessionDecisionsCreate(reportId, {
        decisions: decisionsPayload,
      });
      onComplete?.(summary);
      onClose();
    } catch (error) {
      showSnackbar(formatLiftAPIClientError('Failed to apply supersession decisions', error), {
        variant: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canApply =
    violations.length > 0 &&
    violations.every((v) => isDecisionComplete(decisions[v.id] ?? DEFAULT_DECISION));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{`Open ${violationsTerm} on this ${liftTerm}`}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : violations.length === 0 ? (
          <Typography color="text.secondary">{`No open ${violationsTerm} on this ${liftTerm}.`}</Typography>
        ) : (
          violations.map((v) => {
            const decision = decisions[v.id] ?? DEFAULT_DECISION;
            return (
              <Box
                key={v.id}
                sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography variant="subtitle2">
                  {v.code}: {v.description}
                </Typography>
                <RadioGroup
                  row
                  value={decision.kind}
                  onChange={(e) =>
                    patchDecision(v.id, { kind: e.target.value as SupersessionDecisionKind })
                  }
                >
                  <FormControlLabel
                    value="carry_forward"
                    control={<Radio />}
                    label="Carry forward"
                  />
                  <FormControlLabel
                    value="superseded"
                    control={<Radio />}
                    label="Mark superseded"
                  />
                  <FormControlLabel value="resolved" control={<Radio />} label="Mark resolved" />
                </RadioGroup>
                {decision.kind === 'resolved' && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel>Resolution type</InputLabel>
                        <Select
                          label="Resolution type"
                          value={decision.resolutionType}
                          onChange={(e) => patchDecision(v.id, { resolutionType: e.target.value })}
                        >
                          <MenuItem value="reinspection">Reinspection</MenuItem>
                          <MenuItem value="waiver">Waiver</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="Resolution notes"
                        size="small"
                        fullWidth
                        required
                        error={!decision.resolutionNotes.trim()}
                        value={decision.resolutionNotes}
                        onChange={(e) => patchDecision(v.id, { resolutionNotes: e.target.value })}
                      />
                    </Box>
                    {decision.resolutionType === 'other' && (
                      <TextField
                        label="Custom resolution type"
                        size="small"
                        required
                        error={!decision.customResolutionType.trim()}
                        value={decision.customResolutionType}
                        onChange={(e) =>
                          patchDecision(v.id, { customResolutionType: e.target.value })
                        }
                      />
                    )}
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={BUTTON_SX}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || loading || !canApply}
          sx={BUTTON_SX}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
